import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const DOC_TYPES = [
  { key: 'will',                label: 'Will',                  icon: '📜' },
  { key: 'trust',               label: 'Trust',                 icon: '🏛️' },
  { key: 'power_of_attorney',   label: 'Power of Attorney',     icon: '✍️' },
  { key: 'deed',                label: 'Deed',                  icon: '🏠' },
  { key: 'title',               label: 'Title',                 icon: '🚗' },
  { key: 'passport',            label: 'Passport / ID',         icon: '🛂' },
  { key: 'birth_certificate',   label: 'Birth Certificate',     icon: '👶' },
  { key: 'insurance_policy',    label: 'Insurance Policy',      icon: '🛡️' },
  { key: 'tax_return',          label: 'Tax Return',            icon: '🧾' },
  { key: 'other',               label: 'Other',                 icon: '📄' },
];

const EMPTY_FORM = { document_type: 'will', name: '', notes: '', file: null };

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function Documents({ session }) {
  const [vault, setVault] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [session]);

  const load = async () => {
    const { data: vaultData } = await supabase.from('vaults').select('id').eq('owner_id', session.user.id).single();
    setVault(vaultData);
    if (vaultData) {
      const { data } = await supabase.from('documents').select('*').eq('vault_id', vaultData.id).order('document_type').order('name');
      setDocuments(data || []);
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('File must be under 10MB.');
      return;
    }
    setError('');
    setForm(prev => ({ ...prev, file, name: prev.name || file.name.replace(/\.[^.]+$/, '') }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true); setUploadProgress(0);

    let file_url = null;
    let file_size = null;
    let mime_type = null;

    if (form.file) {
      const ext = form.file.name.split('.').pop();
      const path = `${session.user.id}/${vault.id}/${Date.now()}.${ext}`;
      setUploadProgress(30);

      const { error: uploadErr } = await supabase.storage
        .from('legacy-documents')
        .upload(path, form.file, { cacheControl: '3600', upsert: false });

      if (uploadErr) { setError(uploadErr.message); setSaving(false); setUploadProgress(0); return; }

      setUploadProgress(70);
      const { data: urlData } = supabase.storage.from('legacy-documents').getPublicUrl(path);
      file_url = urlData.publicUrl;
      file_size = form.file.size;
      mime_type = form.file.type;
    }

    setUploadProgress(90);

    const { error: dbErr } = await supabase.from('documents').insert({
      vault_id: vault.id,
      owner_id: session.user.id,
      document_type: form.document_type,
      name: form.name,
      notes: form.notes,
      file_url,
      file_size,
      mime_type,
    });

    if (dbErr) { setError(dbErr.message); setSaving(false); setUploadProgress(0); return; }

    setUploadProgress(100);
    setTimeout(() => setUploadProgress(0), 1000);
    setSuccess(`${form.name} uploaded successfully.`);
    setTimeout(() => setSuccess(''), 3000);
    setSaving(false); setShowForm(false); setForm(EMPTY_FORM);
    load();
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove "${doc.name}"?`)) return;
    setDeleting(doc.id);

    // Delete from storage if there's a file
    if (doc.file_url) {
      const path = doc.file_url.split('/legacy-documents/')[1];
      if (path) await supabase.storage.from('legacy-documents').remove([path]);
    }

    await supabase.from('documents').delete().eq('id', doc.id);
    setDeleting(null); load();
  };

  const handleDownload = async (doc) => {
    if (!doc.file_url) return;
    window.open(doc.file_url, '_blank');
  };

  const handleCancel = () => { setShowForm(false); setForm(EMPTY_FORM); setError(''); setUploadProgress(0); };

  const grouped = DOC_TYPES.map(type => ({
    ...type,
    items: documents.filter(d => d.document_type === type.key),
  })).filter(g => g.items.length > 0);

  const style = `
    .pg { padding: 32px; max-width: 860px; margin: 0 auto; }
    .pg-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
    .pg-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 32px; color: #1E1040; line-height: 1.05; }
    .pg-title em { font-style: italic; color: #8B5CF6; }
    .pg-sub { font-size: 14px; color: #6B7280; margin-top: 6px; line-height: 1.5; }
    .pg-add-btn { display: flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; border: none; border-radius: 100px; padding: 12px 24px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; cursor: pointer; white-space: nowrap; box-shadow: 0 4px 12px rgba(139,92,246,0.25); transition: opacity 0.2s, transform 0.2s; }
    .pg-add-btn:hover { opacity: 0.88; transform: translateY(-1px); }
    .pg-error { background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626; margin-bottom: 16px; }
    .pg-success { background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #16A34A; margin-bottom: 16px; }
    .pg-why { background: linear-gradient(135deg, rgba(139,92,246,0.05), rgba(56,189,248,0.03)); border: 1px solid rgba(139,92,246,0.12); border-radius: 16px; padding: 20px 24px; margin-bottom: 28px; display: flex; gap: 14px; align-items: flex-start; }
    .pg-why-icon { font-size: 24px; flex-shrink: 0; }
    .pg-why-text { font-size: 14px; color: #374151; line-height: 1.6; }
    .pg-why-text strong { color: #1E1040; }

    /* FORM */
    .pg-form { background: #fff; border: 1.5px solid #EAE5F5; border-radius: 20px; padding: 28px; margin-bottom: 28px; box-shadow: 0 4px 20px rgba(139,92,246,0.07); }
    .pg-form-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 20px; color: #1E1040; margin-bottom: 20px; }
    .pg-type-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
    .pg-type-btn { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 100px; border: 1.5px solid #EAE5F5; background: #FDFCFF; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 500; color: #6B7280; cursor: pointer; transition: all 0.15s; }
    .pg-type-btn:hover { border-color: rgba(139,92,246,0.3); color: #7C3AED; }
    .pg-type-btn.selected { border-color: #8B5CF6; background: rgba(139,92,246,0.06); color: #7C3AED; font-weight: 700; }
    .pg-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
    @media(max-width:600px){ .pg-form-grid { grid-template-columns: 1fr; } }
    .pg-field { display: flex; flex-direction: column; gap: 6px; }
    .pg-field.full { grid-column: 1 / -1; }
    .pg-label { font-size: 12px; font-weight: 700; color: #374151; }
    .pg-input, .pg-textarea { padding: 11px 14px; border: 1.5px solid #E5E7EB; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #1E1040; background: #FAFAFA; outline: none; transition: border-color 0.2s; width: 100%; }
    .pg-input:focus, .pg-textarea:focus { border-color: #8B5CF6; background: #fff; }
    .pg-textarea { resize: vertical; min-height: 70px; }
    .pg-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; }
    .pg-btn-cancel { padding: 10px 20px; border: 1.5px solid #EDE9E3; border-radius: 100px; background: transparent; color: #6B7280; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; }
    .pg-btn-save { padding: 10px 24px; border: none; border-radius: 100px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 12px rgba(139,92,246,0.25); transition: opacity 0.2s; }
    .pg-btn-save:hover { opacity: 0.88; }
    .pg-btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

    /* FILE UPLOAD */
    .pg-upload-area {
      border: 2px dashed #C4B5FD; border-radius: 14px; padding: 32px 20px;
      text-align: center; cursor: pointer; transition: all 0.2s;
      background: #FDFCFF; position: relative;
    }
    .pg-upload-area:hover { border-color: #8B5CF6; background: rgba(139,92,246,0.02); }
    .pg-upload-area.has-file { border-color: #22C55E; background: rgba(34,197,94,0.03); }
    .pg-upload-icon { font-size: 32px; margin-bottom: 10px; }
    .pg-upload-text { font-size: 14px; font-weight: 600; color: #7C3AED; margin-bottom: 4px; }
    .pg-upload-hint { font-size: 12px; color: #9CA3AF; }
    .pg-upload-file-name { font-size: 13px; font-weight: 600; color: #16A34A; margin-top: 8px; }
    .pg-upload-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }

    /* PROGRESS BAR */
    .pg-progress-wrap { margin-top: 12px; }
    .pg-progress-label { font-size: 12px; color: #6B7280; margin-bottom: 6px; display: flex; justify-content: space-between; }
    .pg-progress-track { height: 6px; background: #EAE5F5; border-radius: 100px; overflow: hidden; }
    .pg-progress-fill { height: 100%; background: linear-gradient(90deg, #8B5CF6, #38BDF8); border-radius: 100px; transition: width 0.3s ease; }

    /* DOCUMENT LIST */
    .pg-group { margin-bottom: 28px; }
    .pg-group-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #EAE5F5; }
    .pg-group-icon { font-size: 18px; }
    .pg-group-label { font-size: 13px; font-weight: 700; color: #374151; letter-spacing: 0.04em; text-transform: uppercase; }
    .pg-group-count { font-size: 12px; color: #9CA3AF; margin-left: auto; }

    .pg-doc-card { background: linear-gradient(145deg, #FDFCFF, #FAF8FF); border: 1.5px solid #EAE5F5; border-radius: 14px; padding: 18px 20px; margin-bottom: 10px; display: flex; align-items: center; gap: 16px; transition: border-color 0.2s, box-shadow 0.2s; }
    .pg-doc-card:hover { border-color: rgba(139,92,246,0.25); box-shadow: 0 4px 16px rgba(139,92,246,0.08); }
    .pg-doc-icon { width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0; background: linear-gradient(135deg, #8B5CF6, #38BDF8); display: flex; align-items: center; justify-content: center; font-size: 20px; }
    .pg-doc-info { flex: 1; min-width: 0; }
    .pg-doc-name { font-size: 15px; font-weight: 700; color: #1E1040; margin-bottom: 2px; }
    .pg-doc-meta { font-size: 12px; color: #9CA3AF; }
    .pg-doc-notes { font-size: 13px; color: #6B7280; margin-top: 4px; }
    .pg-doc-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .pg-btn-download { padding: 6px 14px; border-radius: 100px; border: 1.5px solid rgba(139,92,246,0.25); background: transparent; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600; color: #7C3AED; cursor: pointer; transition: all 0.15s; }
    .pg-btn-download:hover { background: rgba(139,92,246,0.06); }
    .pg-btn-delete { padding: 6px 14px; border-radius: 100px; border: 1.5px solid transparent; background: transparent; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600; color: #9CA3AF; cursor: pointer; transition: all 0.15s; }
    .pg-btn-delete:hover { border-color: rgba(239,68,68,0.2); color: #EF4444; background: rgba(239,68,68,0.04); }
    .pg-btn-delete:disabled { opacity: 0.4; cursor: not-allowed; }

    /* EMPTY */
    .pg-empty { background: #fff; border: 2px dashed #EAE5F5; border-radius: 20px; padding: 60px 40px; text-align: center; }
    .pg-empty-icon { font-size: 48px; margin-bottom: 16px; }
    .pg-empty-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 22px; color: #1E1040; margin-bottom: 8px; }
    .pg-empty-sub { font-size: 14px; color: #6B7280; line-height: 1.6; max-width: 400px; margin: 0 auto 24px; }
  `;

  if (loading) return <div style={{padding: 32, color: '#6B7280', fontSize: 14}}>Loading...</div>;

  const selectedType = DOC_TYPES.find(t => t.key === form.document_type);

  return (
    <>
      <style>{style}</style>
      <div className="pg">
        <div className="pg-header">
          <div>
            <h1 className="pg-title">Important <em>Documents</em></h1>
            <p className="pg-sub">Upload your wills, trusts, deeds, passports, and other critical documents your family will need.</p>
          </div>
          {!showForm && <button className="pg-add-btn" onClick={() => setShowForm(true)}>+ Upload Document</button>}
        </div>

        {documents.length === 0 && !showForm && (
          <div className="pg-why">
            <div className="pg-why-icon">💡</div>
            <div className="pg-why-text"><strong>Why this matters:</strong> A will filed in a drawer does nothing if your family can't find it. Upload your most important documents here so they're secure, private, and accessible when needed.</div>
          </div>
        )}

        {error && <div className="pg-error">{error}</div>}
        {success && <div className="pg-success">✓ {success}</div>}

        {showForm && (
          <form className="pg-form" onSubmit={handleSubmit}>
            <div className="pg-form-title">Upload a Document</div>

            <div className="pg-type-grid">
              {DOC_TYPES.map(t => (
                <button key={t.key} type="button"
                  className={`pg-type-btn${form.document_type === t.key ? ' selected' : ''}`}
                  onClick={() => setForm({...form, document_type: t.key})}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            <div className="pg-form-grid">
              <div className="pg-field">
                <label className="pg-label">Document Name *</label>
                <input className="pg-input" type="text" placeholder={`My ${selectedType?.label || 'Document'}`}
                  value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              <div className="pg-field">
                <label className="pg-label">Notes</label>
                <input className="pg-input" type="text" placeholder="Version, date, attorney name..."
                  value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
              <div className="pg-field full">
                <label className="pg-label">File (PDF, JPG, PNG — max 10MB)</label>
                <div className={`pg-upload-area${form.file ? ' has-file' : ''}`}>
                  <input type="file" className="pg-upload-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileChange} />
                  <div className="pg-upload-icon">{form.file ? '✅' : '📁'}</div>
                  {form.file ? (
                    <>
                      <div className="pg-upload-file-name">{form.file.name}</div>
                      <div className="pg-upload-hint">{formatBytes(form.file.size)} · Click to change</div>
                    </>
                  ) : (
                    <>
                      <div className="pg-upload-text">Click to choose a file</div>
                      <div className="pg-upload-hint">PDF, JPG, PNG, DOC up to 10MB</div>
                    </>
                  )}
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="pg-progress-wrap">
                    <div className="pg-progress-label">
                      <span>Uploading...</span><span>{uploadProgress}%</span>
                    </div>
                    <div className="pg-progress-track">
                      <div className="pg-progress-fill" style={{width: `${uploadProgress}%`}} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="pg-form-actions">
              <button type="button" className="pg-btn-cancel" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="pg-btn-save" disabled={saving || !form.name}>
                {saving ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </form>
        )}

        {documents.length === 0 && !showForm ? (
          <div className="pg-empty">
            <div className="pg-empty-icon">📄</div>
            <div className="pg-empty-title">No documents yet</div>
            <p className="pg-empty-sub">Upload your will, trust, deeds, passports, and other important documents.</p>
            <button className="pg-add-btn" onClick={() => setShowForm(true)}>+ Upload Your First Document</button>
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.key} className="pg-group">
              <div className="pg-group-header">
                <span className="pg-group-icon">{group.icon}</span>
                <span className="pg-group-label">{group.label}</span>
                <span className="pg-group-count">{group.items.length}</span>
              </div>
              {group.items.map(doc => (
                <div key={doc.id} className="pg-doc-card">
                  <div className="pg-doc-icon">{group.icon}</div>
                  <div className="pg-doc-info">
                    <div className="pg-doc-name">{doc.name}</div>
                    <div className="pg-doc-meta">
                      {doc.file_size ? formatBytes(doc.file_size) : 'No file'} · Added {new Date(doc.created_at).toLocaleDateString()}
                    </div>
                    {doc.notes && <div className="pg-doc-notes">{doc.notes}</div>}
                  </div>
                  <div className="pg-doc-actions">
                    {doc.file_url && (
                      <button className="pg-btn-download" onClick={() => handleDownload(doc)}>View ↗</button>
                    )}
                    <button className="pg-btn-delete" disabled={deleting === doc.id} onClick={() => handleDelete(doc)}>
                      {deleting === doc.id ? '...' : 'Remove'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );
}
